import type { SharePointItem } from '@/lib/api/sharepoint.js';
import type {
  CreateSharePointSourceInput,
  KnowledgeSource,
} from '@/lib/api/workspace-knowledge.js';

/**
 * Where a SharePoint item lives, as the platform addresses it. A Graph `item_id` is unique only WITHIN
 * its drive, so two libraries can hand out the same id for two unrelated items — and the Graph ids
 * themselves are unique only inside one tenant, so once a workspace reaches two SharePoints the drive
 * does not settle it either. Every join over SharePoint items — "is this folder already in the
 * knowledge base", a deep link, a reference in a message — carries all three.
 *
 * `connectionId` is `null` for a surface browsing whichever connection the service resolves, which is
 * how everything worked before connections were named.
 */
export interface SharePointAddress {
  connectionId: string | null;
  driveId: string;
  itemId: string;
}

/**
 * The workspace's knowledge source registered for this exact item, or undefined when there is none.
 * Sources with no SharePoint address (an upload, a shelved workspace file) can never match.
 *
 * Matched on the drive AND the item — matching on the item alone would mark an item as shelved because
 * a different library holds an item with the same id — and on the connection whenever both sides name
 * one. Where either side names none the pair decides alone: a source registered before connections had
 * ids came from the workspace's shared connection and carries nothing to compare, and dropping its mark
 * would invite a second registration of a source the workspace already has (the service answers that
 * with a 409).
 */
export function findSharePointKnowledgeSource(
  sources: readonly KnowledgeSource[],
  address: SharePointAddress,
): KnowledgeSource | undefined {
  return sources.find((source) => {
    if (
      source.config.drive_id !== address.driveId ||
      source.config.item_id !== address.itemId
    ) {
      return false;
    }
    const sourceConnectionId = source.config.connection_id;
    if (!sourceConnectionId || address.connectionId === null) return true;
    return sourceConnectionId === address.connectionId;
  });
}

/**
 * The create body for putting a SharePoint folder or file on the workspace's knowledge shelf. `config` is
 * already the snake_case wire shape the service validates — a camelCase key would be dropped silently —
 * and `name` is Graph's display name, which the knowledge list prefers over the URL's last segment
 * (a classic SharePoint URL ends in `Doc.aspx`).
 */
export function sharePointSourceInput({
  connectionId = null,
  siteId,
  driveId,
  item,
}: {
  /**
   * Which of the workspace's SharePoint connections the item lives in. Sent as `config.connection_id`
   * when there is one, and left out entirely otherwise — the service treats an absent field as "the
   * workspace's shared connection", and an empty string would be a connection id that matches nothing.
   */
  connectionId?: string | null;
  siteId: string;
  driveId: string;
  item: SharePointItem;
}): CreateSharePointSourceInput {
  return {
    type: item.isFolder ? 'sharepoint_folder' : 'sharepoint_file',
    config: {
      site_id: siteId,
      drive_id: driveId,
      item_id: item.itemId,
      ...(connectionId ? { connection_id: connectionId } : {}),
      url: item.webUrl,
      name: item.name,
    },
    enabled: true,
  };
}
